import React, { useState } from 'react';
import { FileCode, Loader2, Download, Check, AlertCircle } from 'lucide-react';

interface DpsXmlExportButtonProps {
  agentId: string;
  field1?: string;
  field2?: string;
  field3?: string;
  field4?: string;
  field5?: string;
  field6?: string;
  notesText?: string;
  className?: string;
  isUk?: boolean;
}

export default function DpsXmlExportButton({
  agentId,
  field1 = '',
  field2 = '',
  field3 = '',
  field4 = '',
  field5 = '',
  field6 = '',
  notesText = '',
  className = '',
  isUk = true
}: DpsXmlExportButtonProps) {
  const [downloading, setDownloading] = useState(false);
  const [downloaded, setDownloaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDownloadXml = async (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (downloading) return;

    setDownloading(true);
    setError(null);

    try {
      let payload: any = null;

      // Extract numeric values from text fields or fallback to 2026 defaults
      const textToScan = `${field1} ${field2} ${field3} ${field4} ${field5} ${field6} ${notesText}`;
      
      // Parse revenue/income amount
      const incomeMatches = textToScan.match(/(?:дохід|доходу|сума|обсяг|income)[\s:—–-]*([\d\s]{3,12}(?:[.,]\d{1,2})?)/i);
      let incomeAmount = 485000.0;
      if (incomeMatches && incomeMatches[1]) {
        const cleanNum = parseFloat(incomeMatches[1].replace(/\s/g, '').replace(',', '.'));
        if (!isNaN(cleanNum) && cleanNum > 0) {
          incomeAmount = cleanNum;
        }
      }

      // Parse TIN / RNOKPP
      const tinMatch = textToScan.match(/\b(\d{10})\b/) || textToScan.match(/\b(\d{8})\b/);
      const tin = tinMatch ? tinMatch[1] : '2713213814'; // Банах А.С. РНОКПП

      if (agentId === 'vat-risk-blocker') {
        // Form J/F1312603 for unblocking suspended tax invoices (SMKOR CMU Res. 1165)
        const pnMatch = textToScan.match(/№\s*(\d+)/i);
        const invoiceNum = pnMatch ? pnMatch[1] : '148';
        
        payload = {
          type: 'smkor_explanation',
          smkorParams: {
            taxpayer: {
              tin,
              fullName: 'Банах Андрій Степанович',
              address: 'м. Київ, вул. Доківська, 10',
              vatNumber: `${tin.padEnd(8, '0').slice(0, 8)}26551`,
              taxOfficeCode: '2655'
            },
            isLegalEntity: tin.length === 8,
            invoiceNumber: invoiceNum,
            invoiceDate: '12.08.2026',
            buyerTin: '39485721',
            buyerName: 'ТОВ «Київ-Буд Девелопмент»',
            supplyVolumeWithoutVat: Math.round(incomeAmount * 0.8333 * 100) / 100,
            vatAmount: Math.round(incomeAmount * 0.1667 * 100) / 100,
            goodsUktzedCode: '7214 20 00 00',
            goodsDescription: field2 || 'Арматура сталева гарячекатана Ø12 мм',
            explanationText: notesText || 'Письмові пояснення платника податків щодо підтвердження реальності господарської операції за ПКМУ № 1165 та правовими позиціями Верховного Суду.',
            signatoryFullName: 'Банах Андрій Степанович'
          }
        };
      } else {
        // Default: Form F0103308 for FOP Single Tax (Group 3 with 1% Military Tax per Law No. 4015-IX)
        payload = {
          type: 'fop_declaration',
          fopParams: {
            taxpayer: {
              tin,
              fullName: 'Банах Андрій Степанович',
              address: 'м. Київ, вул. Доківська, 10, корпус 1, кв. 4',
              kved: '62.01',
              taxOfficeCode: '2655'
            },
            group: 3,
            periodYear: 2026,
            periodQuarter: 1,
            incomeAmount,
            taxRatePercent: 5.0,
            militaryTaxRatePercent: 1.0, // Закон № 4015-IX
            esvQuarterlyAmount: 5707.02, // 3 * 1902.34 грн (2026 р. при мінзарплаті 8 647 грн)
            signatoryFullName: 'Банах Андрій Степанович'
          }
        };
      }

      const response = await fetch('/api/tax/generate-dps-xml', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || (isUk ? 'Помилка формування XML' : 'Failed to generate XML'));
      }

      const data = await response.json();
      if (!data.xmlContent) {
        throw new Error(isUk ? 'Сервер не повернув XML' : 'No XML content returned');
      }

      // Download file to user browser
      const blob = new Blob([data.xmlContent], { type: 'application/xml;charset=windows-1251' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = data.fileName || `${agentId}_dps_report_2026.xml`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      setDownloaded(true);
      setTimeout(() => setDownloaded(false), 4000);
    } catch (err: any) {
      console.error('XML Download error:', err);
      setError(err.message || (isUk ? 'Помилка генерації XML' : 'Error generating XML'));
      setTimeout(() => setError(null), 5000);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="relative inline-block">
      <button
        onClick={handleDownloadXml}
        disabled={downloading}
        type="button"
        title={isUk ? 'Завантажити офіційний файл XML для подання через Електронний кабінет ДПС або M.E.Doc' : 'Download official XML file for Ukrainian State Tax Service (DPS) Electronic Cabinet'}
        className={`px-3.5 py-2 bg-amber-600 hover:bg-amber-700 active:bg-amber-800 text-white text-xs font-extrabold uppercase rounded-lg transition-all shadow-xs flex items-center gap-1.5 cursor-pointer disabled:opacity-50 border border-amber-500 font-mono ${className}`}
      >
        {downloading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin text-amber-200" />
            <span>{isUk ? 'Формування XML...' : 'Generating XML...'}</span>
          </>
        ) : downloaded ? (
          <>
            <Check className="w-4 h-4 text-emerald-300" />
            <span>{isUk ? '✓ XML Збережено!' : '✓ XML Saved!'}</span>
          </>
        ) : (
          <>
            <FileCode className="w-4 h-4 text-amber-200" />
            <span>{isUk ? 'XML для ДПС' : 'DPS XML'}</span>
          </>
        )}
      </button>

      {error && (
        <div className="absolute top-full mt-1.5 right-0 z-50 p-2.5 bg-red-900/95 text-red-100 text-[11px] rounded-lg border border-red-500 shadow-xl max-w-xs flex items-center gap-2 font-mono whitespace-nowrap">
          <AlertCircle className="w-4 h-4 text-red-300 shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}
